import type { TWifiFailReason } from '@/helpers/androidWifiProvisioning'

export type TWifiSetupPhase
  = | 'status'
    | 'edit'
    | 'applying'
    | 'success'
    | 'error'
    | 'notOwner'
    | 'unsupported'
    | 'forgetConfirm'

export interface TWifiSetupState {
  phase: TWifiSetupPhase
  ssidDraft: string
  pskDraft: string
  showPsk: boolean
  showErrors: boolean
  errorReason: TWifiFailReason | null
  savedSsid: string | null
  applyStep: number
}

export type TWifiSetupEvent
  = | { type: 'showStatus', savedSsid: string | null }
    | { type: 'notOwner' }
    | { type: 'unsupported' }
    | { type: 'editFresh' }
    | { type: 'editKeepSsid' }
    | { type: 'ssid', value: string }
    | { type: 'psk', value: string }
    | { type: 'toggleShow' }
    | { type: 'showFieldErrors' }
    | { type: 'apply' }
    | { type: 'step', step: number }
    | { type: 'result', ok: boolean, reason: TWifiFailReason | null, ssid: string }
    | { type: 'forgetAsk' }
    | { type: 'forgetCancel' }
    | { type: 'forgot' }

export function initialWifiSetupState(): TWifiSetupState {
  return {
    phase: 'status',
    ssidDraft: '',
    pskDraft: '',
    showPsk: false,
    showErrors: false,
    errorReason: null,
    savedSsid: null,
    applyStep: 0
  }
}

export function wifiSetupReducer(state: TWifiSetupState, event: TWifiSetupEvent): TWifiSetupState {
  switch (event.type) {
    case 'showStatus':
      return {
        ...state,
        phase: 'status',
        savedSsid: event.savedSsid,
        pskDraft: '',
        showPsk: false,
        showErrors: false,
        errorReason: null
      }
    case 'notOwner':
      return { ...state, phase: 'notOwner', pskDraft: '', showPsk: false }
    case 'unsupported':
      return { ...state, phase: 'unsupported', pskDraft: '', showPsk: false }
    case 'editFresh':
      return {
        ...state,
        phase: 'edit',
        ssidDraft: state.savedSsid ?? '',
        pskDraft: '',
        showPsk: false,
        showErrors: false,
        errorReason: null
      }
    case 'editKeepSsid':
      return {
        ...state,
        phase: 'edit',
        pskDraft: '',
        showPsk: false,
        showErrors: false,
        errorReason: null
      }
    case 'ssid':
      return { ...state, ssidDraft: event.value }
    case 'psk':
      return { ...state, pskDraft: event.value }
    case 'toggleShow':
      return { ...state, showPsk: !state.showPsk }
    case 'showFieldErrors':
      return { ...state, showErrors: true }
    case 'apply':
      return { ...state, phase: 'applying', applyStep: 0, showPsk: false, pskDraft: state.pskDraft }
    case 'step':
      return { ...state, applyStep: event.step }
    case 'result': {
      if (!event.ok && event.reason === 'notOwner') {
        return { ...state, phase: 'notOwner', pskDraft: '', showPsk: false, errorReason: 'notOwner' }
      }
      const persisted = event.ok || event.reason === 'noInternet'
      return {
        ...state,
        phase: event.ok ? 'success' : 'error',
        errorReason: event.ok ? null : event.reason,
        pskDraft: '',
        showPsk: false,
        applyStep: 3,
        savedSsid: persisted ? event.ssid : state.savedSsid
      }
    }
    case 'forgetAsk':
      return { ...state, phase: 'forgetConfirm' }
    case 'forgetCancel':
      return { ...state, phase: 'status' }
    case 'forgot':
      return {
        ...state,
        phase: 'status',
        savedSsid: null,
        ssidDraft: '',
        pskDraft: '',
        showPsk: false
      }
    default:
      return state
  }
}
